import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Attendance Calendar Component
 * Visual month view with color-coded attendance dots
 * Green = Present, Red = Absent, Yellow = Half-day
 */
const AttendanceCalendar = ({ attendanceRecords = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get month details
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString("default", { month: "long" });

  // Get first day of month and total days
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Navigation
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Create attendance map for quick lookup
  const attendanceMap = {};
  attendanceRecords.forEach((record) => {
    const date = new Date(record.date);
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    attendanceMap[key] = record.status;
  });

  // Get attendance status for a specific day
  const getAttendanceStatus = (day) => {
    const key = `${year}-${month}-${day}`;
    return attendanceMap[key];
  };

  // Get color for status
  const getStatusColor = (status) => {
    const colors = {
      Present: "bg-green-500",
      Absent: "bg-red-500",
      "Half-day": "bg-yellow-500",
    };
    return colors[status] || "bg-gray-300";
  };

  // Get status emoji
  const getStatusEmoji = (status) => {
    const emojis = {
      Present: "✓",
      Absent: "✗",
      "Half-day": "½",
    };
    return emojis[status] || "";
  };

  // Calculate attendance percentage for current month
  const calculateMonthlyPercentage = () => {
    const recordsInMonth = attendanceRecords.filter((record) => {
      if (!record.date) return false; // Skip empty dates

      const date = new Date(record.date);

      // TRAP FIX: If your 'month' variable is 1-12 (Jan-Dec), uncomment the line below:
      // const targetMonth = month - 1;
      // Otherwise, if 'month' is already 0-11, leave it as is:
      const targetMonth = month;

      return date.getMonth() === targetMonth && date.getFullYear() === year;
    });

    if (recordsInMonth.length === 0) return 0;

    const presentDays = recordsInMonth.filter((r) => {
      // TRAP FIX: Fallback to empty string if status is null, and convert to lowercase
      const status = (r.status || "").toLowerCase();

      return (
        status.includes("present") ||
        status.includes("half-day") ||
        status.includes("half day")
      );
    }).length;

    // Better debugging:

    return Math.round((presentDays / recordsInMonth.length) * 100);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const status = getAttendanceStatus(day);
      const isToday =
        day === new Date().getDate() &&
        month === new Date().getMonth() &&
        year === new Date().getFullYear();

      days.push(
        <div
          key={day}
          className={`
            relative p-2 text-center rounded-lg transition-all
            ${isToday ? "ring-2 ring-blue-500" : ""}
            ${status ? "cursor-pointer hover:bg-gray-100" : "text-gray-400"}
          `}
        >
          <div className="text-sm font-medium">{day}</div>
          {status && (
            <div className="flex items-center justify-center mt-1">
              <div
                className={`w-3 h-3 rounded-full ${getStatusColor(status)}`}
                title={status}
              />
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  const monthlyPercentage = calculateMonthlyPercentage();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CalendarIcon className="w-6 h-6" style={{ color: "#2C5F7F" }} />
          <h3 className="text-xl font-bold" style={{ color: "#2C5F7F" }}>
            {monthName} {year}
          </h3>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={goToPreviousMonth}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            onClick={goToToday}
            className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2"
          >
            Today
          </Button>
          <Button
            onClick={goToNextMonth}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Monthly Stats */}
      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Monthly Attendance</p>
            <p className="text-3xl font-bold" style={{ color: "#2C5F7F" }}>
              {monthlyPercentage}%
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-600">Present</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-gray-600">Half-day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-gray-600">Absent</span>
            </div>
          </div>
        </div>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="text-center text-sm font-semibold text-gray-600 p-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">{generateCalendarDays()}</div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Click on dates with attendance records to view details
        </p>
      </div>
    </div>
  );
};

export default AttendanceCalendar;
